// Iter 306 — partner-side pro forma card: this month's total, Accept,
// "Have a question" / "Request review", corrected versions and the thread.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileCheck2, Flag, HelpCircle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { money } from "./engineShared";
import { InquiryDialog } from "./InquiryDialog";
import { InquiryThread } from "./InquiryThread";

export function ProformaActionsCard({ periodLabel }) {
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [flow, setFlow] = useState(null);
  const [dialog, setDialog] = useState("");       // question | review
  const [accepting, setAccepting] = useState(false);
  const period = periodLabel || summary?.period_label;
  const load = async () => {
    try {
      const { data: s } = await api.get(`/commissions/my-summary${periodLabel ? `?period_label=${encodeURIComponent(periodLabel)}` : ""}`);
      setSummary(s);
      if (s?.visible && s.partner_key && s.period_label) {
        const { data: f } = await api.get(`/commissions/partners/${encodeURIComponent(s.partner_key)}/periods/${encodeURIComponent(s.period_label)}/workflow`);
        setFlow(f);
      }
    } catch { setSummary({ visible: false }); }
  };
  useEffect(() => { load(); }, [periodLabel]);
  if (!summary || summary.visible === false || !summary.partner_key) return null;

  const accept = async () => {
    const v = flow?.version > 1 ? ` (corrected version ${flow.version})` : "";
    if (!window.confirm(`Accept the ${period} pro forma${v} for ${money(summary.total)}? Amount changes are then locked and Accounting is notified.`)) return;
    setAccepting(true);
    try {
      await api.post(`/commissions/partners/${encodeURIComponent(summary.partner_key)}/periods/${encodeURIComponent(period)}/accept`, {});
      toast.success(`Accepted ${period}`); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to accept"); } finally { setAccepting(false); }
  };
  const acc = flow?.acceptance;
  const openInq = (flow?.inquiries || []).filter((i) => i.status !== "resolved");
  const focusId = searchParams.get("inquiry");
  const threads = (flow?.inquiries || []).filter((i) => i.status !== "resolved" || i.inquiry_id === focusId).slice(0, 5);
  return (
    <Card className="border-slate-200 shadow-sm p-4 mb-4" data-testid="proforma-card">
      <div className="flex flex-wrap items-center gap-3">
        <FileCheck2 className="h-5 w-5 text-primary" />
        <div>
          <div className="text-sm font-semibold text-slate-800">Pro forma — {period}{flow?.version > 1 && <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700" data-testid="proforma-version">corrected v{flow.version}</span>}</div>
          <div className="text-xs text-slate-500">
            {summary.deals_count} deals · total <b className="text-slate-800">{money(summary.total)}</b>
            {summary.paid > 0 && <> · paid {money(summary.paid)}</>}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {acc ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700" data-testid="proforma-accepted">
              <ShieldCheck className="h-3.5 w-3.5" /> Accepted {acc.accepted_at?.slice(0, 10)} by {acc.accepted_by_name}
            </span>
          ) : (
            <>
              {openInq.length > 0 && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800" data-testid="proforma-under-review">{openInq.some((i) => i.kind === "review") ? "Under review" : "Question open"}</span>}
              {flow?.superseded && <span className="rounded-full bg-violet-100 px-2 py-1 text-xs font-medium text-violet-700" data-testid="proforma-reaccept">Corrected — please accept again</span>}
              <Button size="sm" variant="outline" onClick={() => setDialog("question")} data-testid="proforma-question"><HelpCircle className="h-3.5 w-3.5" /> Have a question</Button>
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => setDialog("review")} data-testid="proforma-request-review"><Flag className="h-3.5 w-3.5" /> Request review</Button>
              {summary.total > 0 && (
                <Button size="sm" onClick={accept} disabled={accepting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="proforma-accept">
                  {accepting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShieldCheck className="h-3.5 w-3.5" /> Accept pro forma</>}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {flow?.issues?.length > 0 && (
        <div className="mt-3 rounded-md bg-violet-50 p-2 text-xs text-violet-900" data-testid="proforma-issues">
          {flow.issues.map((i) => (
            <div key={i.issue_id}>v{i.version} issued {i.issued_at?.slice(0, 10)} — {i.reason} ({i.previous_total != null ? `${money(i.previous_total)} → ` : ""}{money(i.new_total)})</div>
          ))}
        </div>
      )}
      {threads.length > 0 && (
        <div className="mt-3 space-y-2">
          {threads.map((inq) => (
            <InquiryThread key={inq.inquiry_id} inquiry={inq} isStaff={false}
                           onChange={(fresh) => setFlow((f) => ({ ...f, inquiries: f.inquiries.map((x) => (x.inquiry_id === fresh.inquiry_id ? fresh : x)) }))} />
          ))}
        </div>
      )}
      {dialog && <InquiryDialog partnerKey={summary.partner_key} period={period} kind={dialog} onClose={() => setDialog("")} onCreated={load} />}
    </Card>
  );
}
